import React, { useState, useCallback, useEffect, useRef } from 'react';
import Confetti from 'react-dom-confetti';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Typography, Tooltip } from '@mui/material';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import ContainerLoadingIndicator from 'src/components/common/loading/ContainerLoadingIndicator';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import PageWithLayout from 'src/components/layout/PageWithLayout';
import {
	clearAnnotationUnsavedData,
	setAnnotationUnsavedData,
} from 'src/features/annotation_template/store';
import {
	createAnnotationTemplate,
	getAnnotationTemplate,
	ANNOTATION_TEMPLATE_LOADING_KEY,
} from 'src/features/annotation_template/store/annotationTemplateThunks';
import { QuestionnaireBlock } from 'src/features/questionnaire_template/components/QuestionnaireBlock';
import {
	IQuestionnaireTemplateStateData,
	IQuestionnaireTemplateValidation,
} from 'src/features/questionnaire_template/domain/types';
import {
	questionnaireTemplateService,
	transformAPIResponseToStateStructure,
	transformStateStructureToAPIRequest,
} from 'src/features/questionnaire_template/service/questionnaireTemplateService';
import { AppDispatch, RootState } from 'src/store';
import { deepOmit } from 'src/utils/formatter';

export const AnnotationTemplateBuilderPage: React.FC = () => {
	const { siteSlug, annotationTemplateId } = useParams<{
		siteSlug: string;
		annotationTemplateId: string;
	}>();
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const location = useLocation();

	const annotationUnsavedData = useSelector(
		(state: RootState) => state.annotation_template.unsavedData,
	);
	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[ANNOTATION_TEMPLATE_LOADING_KEY],
	);

	const generatedIndex = String(location.state?.nextGeneratedIndex || '');
	const initialAnnotationData = {
		name: '',
		index: generatedIndex,
		published: false,
		sections: {},
	};
	const getDataForCreateMode = () => {
		if (!annotationUnsavedData) return initialAnnotationData;

		if (!generatedIndex || annotationUnsavedData.index === generatedIndex) {
			return annotationUnsavedData;
		}

		return { ...annotationUnsavedData, index: generatedIndex };
	};

	const [showConfetti, setShowConfetti] = useState(false);
	const [mode, setMode] = useState<'edit' | 'create'>(
		annotationTemplateId ? 'edit' : 'create',
	);
	const [
		initialAnnotationDataOnEditMode,
		setInitialAnnotationDataOnEditMode,
	] = useState<IQuestionnaireTemplateStateData | undefined>(
		location.state?.initialEditModeData,
	);

	const [annotationData, setAnnotationData] = useState<
		IQuestionnaireTemplateStateData | undefined
	>(
		mode === 'edit'
			? location.state?.initialEditModeData
			: getDataForCreateMode(),
	);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

	const [annotationValidation, setAnnotationValidation] =
		useState<IQuestionnaireTemplateValidation>({});
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const { validateRequiredData } = questionnaireTemplateService({
		questionnaireData: annotationData,
		setQuestionnaireValidation: setAnnotationValidation,
	});

	const breadcrumbs = [
		{
			label: siteDetails?.name || 'Site Details',
			href: `/site/${siteSlug}`,
		},
		{
			label: 'Annotation Templates',
			href: `/site/${siteSlug}/annotation-templates`,
		},
		{
			label:
				mode === 'create'
					? 'Create Annotation Template'
					: 'Edit Annotation Template',
		},
	];

	const handleAnnotationChange = useCallback(
		(data: IQuestionnaireTemplateStateData) => {
			setAnnotationData(data);
		},
		[],
	);

	const handleAnnotationDelete = useCallback(() => {
		if (annotationUnsavedData && !annotationTemplateId) {
			dispatch(clearAnnotationUnsavedData());
			setAnnotationData(initialAnnotationData);
			setHasUnsavedChanges(false);
		}
	}, [dispatch, clearAnnotationUnsavedData]);

	const handleSave = () => {
		if (!hasUnsavedChanges) {
			snackbar('No changes detected to submit', 'info', 'center');
			return;
		}

		const isValid = validateRequiredData();
		if (isValid && annotationData) {
			const requestBody = transformStateStructureToAPIRequest(
				annotationData,
				{ includeRequiredForCompletion: false },
			);

			dispatch(
				createAnnotationTemplate({
					requestBody,
					siteSlug: siteSlug!,
					onSuccess: (response) => {
						triggerConfetti();
						const stateData =
							transformAPIResponseToStateStructure(response);

						if (mode === 'create') {
							dispatch(clearAnnotationUnsavedData());
							snackbar(
								'Annotation template created successfully',
								'success',
								'center',
							);
							navigate(
								`/site/${siteSlug}/annotation-template-builder/${response.id}`,
								{
									state: {
										initialEditModeData: stateData,
										showConfetti: true,
									},
								},
							);
						} else if (mode === 'edit') {
							snackbar(
								'Annotation template updated successfully',
								'success',
								'center',
							);
							clearLocationStateOnEditMode('initialEditModeData');
							setInitialAnnotationDataOnEditMode(stateData);
							setAnnotationData(stateData);
							setAnnotationValidation({});
							setHasUnsavedChanges(false);
						}
					},
				}),
			);
		} else if (!isValid) {
			snackbar(
				'Form is invalid. Please update before saving',
				'warning',
				'center',
			);
		}
	};

	const triggerConfetti = () => {
		setShowConfetti(true);
		setTimeout(() => setShowConfetti(false), 3000);
	};

	const clearLocationStateOnEditMode = (key: string) => {
		if (!location.state || !(key in location.state)) return;

		const newState = { ...location.state };
		delete newState[key];

		window.history.replaceState(
			{ ...window.history.state, usr: newState },
			'',
		);
	};

	useEffect(() => {
		if (annotationTemplateId) {
			if (mode === 'create') setMode('edit');
			if (location.state?.initialEditModeData) return;

			dispatch(
				getAnnotationTemplate({
					questionnaireTemplateId: Number(annotationTemplateId),
					siteSlug: siteSlug!,
					onSuccess: (response) => {
						const stateData =
							transformAPIResponseToStateStructure(response);

						setInitialAnnotationDataOnEditMode(stateData);
						setAnnotationData(stateData);
					},
				}),
			);
		} else {
			if (mode === 'edit') setMode('create');
		}
	}, [annotationTemplateId]);

	useDeepCompareEffect(() => {
		if (!annotationData) return;

		const cleanedData = deepOmit(annotationData, ['is_updated']);
		const base =
			mode === 'edit'
				? initialAnnotationDataOnEditMode
				: initialAnnotationData;

		const hasChanges = JSON.stringify(base) !== JSON.stringify(cleanedData);

		setHasUnsavedChanges(hasChanges);
	}, [
		annotationData,
		initialAnnotationDataOnEditMode,
		initialAnnotationData,
		mode,
	]);

	useDeepCompareEffect(() => {
		if (debounceRef.current) clearTimeout(debounceRef.current);

		debounceRef.current = setTimeout(() => {
			if (annotationData && mode === 'create') {
				dispatch(setAnnotationUnsavedData(annotationData));
			}
		}, 350);

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [annotationData, dispatch, setAnnotationUnsavedData]);

	useEffect(() => {
		if (location.state?.showConfetti) {
			triggerConfetti();
			clearLocationStateOnEditMode('showConfetti');
		}
	}, [location.state?.showConfetti]);

	return (
		<PageWithLayout breadcrumbs={breadcrumbs}>
			<Box
				sx={{
					backgroundColor: 'white',
					marginTop: '20px',
					padding: 3,
					borderRadius: '4px',
					position: 'relative',
				}}
			>
				<Typography
					sx={{
						fontFamily: 'Inter',
						fontWeight: 400,
						fontSize: '20px',
						lineHeight: '100%',
						letterSpacing: '0.15px',
						mb: 2,
						textAlign: 'left',
					}}
				>
					Annotation Template
				</Typography>

				<ContainerLoadingIndicator
					loadingKey={ANNOTATION_TEMPLATE_LOADING_KEY}
				/>

				{hasUnsavedChanges && (
					<Box
						sx={{
							position: 'sticky',
							top: 0,
							backgroundColor: 'rgba(255, 138, 0, 0.2)',
							color: '#E53326',
							padding: '6px 12px',
							fontSize: '13px',
							fontWeight: 500,
							borderBottom: '1px solid rgb(250, 187, 93)',
							marginBottom: '5px',
							zIndex: 10,
							backdropFilter: 'blur(4px)',
						}}
					>
						<WarningAmberIcon sx={{ fontSize: 18, mr: 1 }} />
						<span>
							Unsaved changes detected
							{mode === 'edit' && (
								<>
									{' '}
									–{' '}
									<span
										style={{
											fontSize: '12px',
										}}
									>
										changes will be lost unless saved.
									</span>
								</>
							)}
						</span>
					</Box>
				)}

				{annotationData && (
					<>
						<QuestionnaireBlock
							mode={mode}
							defaultData={annotationData}
							onChange={handleAnnotationChange}
							validation={annotationValidation}
							onDelete={handleAnnotationDelete}
							showRequiredForCompletion={false}
						/>

						<Box
							sx={{
								display: 'flex',
								justifyContent: 'flex-end',
								mt: 2,
							}}
						>
							<Tooltip
								title={
									hasUnsavedChanges
										? 'Hanging by a thread - SAVE ME !'
										: ''
								}
								placement='top'
								arrow
							>
								<PrimaryButton
									isLoading={isLoading}
									onClick={() => {
										handleSave();
									}}
									fullWidth={false}
									sx={{ backgroundColor: '#00000099' }}
								>
									Save Template
								</PrimaryButton>
							</Tooltip>
						</Box>
					</>
				)}

				<Box
					sx={{
						position: 'absolute',
						bottom: 10,
						left: '50%',
						transform: 'translateX(-50%)',
						height: 0,
						pointerEvents: 'none',
					}}
				>
					<Confetti
						active={showConfetti}
						config={{
							angle: 90,
							spread: 120,
							startVelocity: 35,
							elementCount: 100,
							dragFriction: 0.1,
							duration: 3000,
							stagger: 3,
							colors: [
								'#ff0a54',
								'#ff477e',
								'#ff7096',
								'#ff85a1',
							],
						}}
					/>
				</Box>
			</Box>
		</PageWithLayout>
	);
};
